import React, { useState, useEffect, useRef } from "react";
import {
  Save,
  Edit,
  Plus,
  CheckCircle2,
  XCircle,
  User,
  Calendar,
  ClipboardList,
  Package,
  Truck,
  Users,
  FileText,
  Clock,
  Loader2,
  ChevronDown,
  Copy,
  Search,
  AlertCircle,
  MapPin,
  Download,
} from "lucide-react";
import * as XLSX from "xlsx";

import CommonService from "../../service/CommonService";
import {
  ADMIN_OPTIONS,
  END_USER_OPTIONS,
  CHASER_OPTIONS,
  STATUS_OPTIONS,
  AREA_OPTIONS,
  defaultForm,
  generateId,
  getBadgeClasses,
  extractLetterFromPoNo,
  getAdminByLetter,
  formatDateForApi,
  normalizeStatusFromApi,
  mapStatusForApi,
  normalizeMocValue,
  splitJobNoValue,
  extractServiceNo,
  InjectStyles,
  inputSx,
  Field,
  SearchableSelect,
} from "./Shared";

const getAreaLabel = (areaId) => {
  if (!areaId) return "-";
  const area = AREA_OPTIONS.find(a => a.value === String(areaId));
  return area ? area.label : areaId;
};

const formFields = [
  ["moc", "MOC", "moc"],
  ["jobNo", "Job No", "text"],
  ["description", "Description *", "textarea"],
  ["momQuantity", "Quantity", "readonly"],
  ["momDescription", "Material Description", "readonly"],
  ["momUnitPrice", "Unit Price", "readonly"],
  ["area", "Area *", "area"],
  ["supplierName", "Supplier Name & Location", "text"],
  ["status", "Status", "status"],
];


function PoCombobox({ value, onChange, options = [], searchResults = [], onSelectResultItem, loading, poSearchLoading, poSearchResult }) {
  const [open, setOpen] = useState(false);
  const [inputVal, setInputVal] = useState(value || "");
  const containerRef = useRef(null);

  useEffect(() => {
    setInputVal(value || "");
  }, [value]);

  useEffect(() => {
    function onDoc(e) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const filteredOptions = inputVal.trim()
    ? options.filter((o) =>
        String(o).toLowerCase().includes(inputVal.toLowerCase())
      )
    : options.slice(0, 40);

  const handleInputChange = (e) => {
    const v = e.target.value;
    setInputVal(v);
    onChange(v);
    setOpen(true);
  };

  const handleSelectOption = (opt) => {
    setInputVal(opt);
    onChange(opt);
    setOpen(false);
  };

  const handleSelectResult = (item) => {
    if (item && item.PO_NO) {
      setInputVal(item.PO_NO);
    }
    if (onSelectResultItem) {
      onSelectResultItem(item);
    }
    setOpen(false);
  };

  const handleClear = () => {
    setInputVal("");
    onChange("");
    setOpen(false);
  };

  const hasApiResults = searchResults && searchResults.length > 0;

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      {/* Text input */}
      <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
        <input
          id="po-poNo-form"
          type="text"
          className="cdp-input"
          style={{
            border: "1.5px solid #e2e8f0",
            borderRadius: 10,
            padding: "9px 38px 9px 12px",
            fontSize: 14,
            outline: "none",
            color: "#1e293b",
            background: "#fff",
            transition: "border-color 0.2s",
            fontFamily: "inherit",
            width: "100%",
            height: 40,
            boxSizing: "border-box",
          }}
          placeholder={loading ? "Loading..." : "Type or select PO No"}
          value={inputVal}
          onChange={handleInputChange}
          onFocus={() => setOpen(true)}
          autoComplete="off"
        />
        {/* Right-side icon area */}
        <div style={{
          position: "absolute",
          right: 10,
          top: "50%",
          transform: "translateY(-50%)",
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}>
          {poSearchLoading ? (
            <span style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 11, color: "#004AAD" }}>
              <Loader2 size={13} className="cdp-spin" />
            </span>
          ) : inputVal ? (
            <button
              type="button"
              onClick={handleClear}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
                display: "flex",
                alignItems: "center",
                color: "#94a3b8",
                fontSize: 14,
                lineHeight: 1,
              }}
              aria-label="Clear PO No"
            >
              ✕
            </button>
          ) : (
            <span style={{ color: "#94a3b8", fontSize: 13 }}>▾</span>
          )}
        </div>
      </div>

      {/* Dropdown suggestions */}
      {open && (
        <div style={{
          position: "absolute",
          left: 0,
          right: 0,
          zIndex: 200,
          top: "calc(100% + 4px)",
          background: "#fff",
          borderRadius: 10,
          boxShadow: "0 8px 30px rgba(2,6,23,0.14)",
          border: "1px solid rgba(0,74,173,0.1)",
          maxHeight: 240,
          overflowY: "auto",
          padding: 4,
        }}>
          {hasApiResults ? (
            <>
              <div style={{
                fontSize: 10,
                fontWeight: 700,
                color: "#004AAD",
                textTransform: "uppercase",
                padding: "4px 8px",
                borderBottom: "1px solid #f1f5f9",
                marginBottom: 2
              }}>
                Matching PO Records ({searchResults.length})
              </div>
              {searchResults.map((item, i) => {
                const mocVal = item.MOCNO || item.MOC_NO || item.MOC || "";
                return (
                  <div
                    key={item.PO_NO ? `${item.PO_NO}_${i}` : i}
                    onMouseDown={(e) => { e.preventDefault(); handleSelectResult(item); }}
                    style={{
                      padding: "8px 10px",
                      borderRadius: 7,
                      cursor: "pointer",
                      fontSize: 12,
                      color: "#1e293b",
                      transition: "background 0.15s",
                      borderBottom: i < searchResults.length - 1 ? "1px solid #f8fafc" : "none",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(0,74,173,0.07)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                  >
                    <div style={{ fontWeight: 700, color: "#004AAD", display: "flex", justifyContent: "space-between" }}>
                      <span>{item.PO_NO || "No PO"}</span>
                      {mocVal && <span style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>MOC: {mocVal}</span>}
                    </div>
                    {item.SUPPLIER_NAME && (
                      <div style={{ fontSize: 11, color: "#475569", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        Supplier: {item.SUPPLIER_NAME}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          ) : filteredOptions.length > 0 ? (
            filteredOptions.map((opt) => (
              <div
                key={opt}
                onMouseDown={(e) => { e.preventDefault(); handleSelectOption(opt); }}
                style={{
                  padding: "8px 12px",
                  borderRadius: 7,
                  cursor: "pointer",
                  fontSize: 13,
                  color: "#1e293b",
                  fontWeight: opt === value ? 700 : 400,
                  background: opt === value ? "rgba(0,74,173,0.07)" : "transparent",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(0,74,173,0.07)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = opt === value ? "rgba(0,74,173,0.07)" : "transparent"; }}
              >
                {opt}
              </div>
            ))
          ) : (
            <div style={{ padding: "8px 12px", fontSize: 12, color: "#94a3b8" }}>
              No matching options
            </div>
          )}
        </div>
      )}

      {/* Feedback: supplier found */}
      {/* {!poSearchLoading && poSearchResult && value && (
        <div style={{
          marginTop: 5,
          padding: "6px 10px",
          background: "rgba(16,185,129,0.08)",
          border: "1px solid rgba(16,185,129,0.3)",
          borderRadius: 8,
          fontSize: 11,
          color: "#065f46",
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}>
          <CheckCircle2 size={12} color="#10b981" />
          <span>
            <b>Supplier:</b> {poSearchResult.SUPPLIER_NAME || "-"}
            {(poSearchResult.MOCNO || poSearchResult.MOC_NO) && (
              <span style={{ marginLeft: 8 }}>
                <b>MOC:</b> {poSearchResult.MOCNO || poSearchResult.MOC_NO}
              </span>
            )}
          </span>
        </div>
      )} */}

      {/* Feedback: not found */}
      {/* {!poSearchLoading && !poSearchResult && value && (
        <div style={{
          marginTop: 5,
          padding: "4px 10px",
          background: "rgba(245,158,11,0.08)",
          border: "1px solid rgba(245,158,11,0.25)",
          borderRadius: 8,
          fontSize: 11,
          color: "#92400e",
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}>
          <AlertCircle size={12} color="#f59e0b" />
          No supplier found for this PO No
        </div>
      )} */}
    </div>
  );
}

// ─── MOC Combobox ─────────────────────────────────────────────────────────────

function MocCombobox({ value, onChange, options = [], searchResults = [], onSelectResultItem, loading, mocSearchLoading, mocSearchResult }) {
  const [open, setOpen] = useState(false);
  const [inputVal, setInputVal] = useState(value || "");
  const containerRef = useRef(null);

  useEffect(() => {
    setInputVal(value || "");
  }, [value]);

  useEffect(() => {
    function onDoc(e) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const filteredOptions = inputVal.trim()
    ? options.filter((o) =>
        String(o).toLowerCase().includes(inputVal.toLowerCase())
      )
    : options.slice(0, 40);

  const handleInputChange = (e) => {
    const v = e.target.value;
    setInputVal(v);
    onChange(v);
    setOpen(true);
  };

  const handleSelectOption = (opt) => {
    setInputVal(opt);
    onChange(opt);
    setOpen(false);
  };

  const handleSelectResult = (item) => {
    const mocVal = item.MOCNO || item.MOC_NO || item.MOC || "";
    if (mocVal) {
      setInputVal(mocVal);
    }
    if (onSelectResultItem) {
      onSelectResultItem(item);
    }
    setOpen(false);
  };

  const handleClear = () => {
    setInputVal("");
    onChange("");
    setOpen(false);
  };

  const hasApiResults = searchResults && searchResults.length > 0;

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
        <input
          type="text"
          className="cdp-input"
          style={{
            border: "1.5px solid #e2e8f0",
            borderRadius: 10,
            padding: "9px 38px 9px 12px",
            fontSize: 14,
            outline: "none",
            color: "#1e293b",
            background: "#fff",
            transition: "border-color 0.2s",
            fontFamily: "inherit",
            width: "100%",
            height: 40,
            boxSizing: "border-box",
          }}
          placeholder={loading ? "Loading..." : "Type or select MOC No"}
          value={inputVal}
          onChange={handleInputChange}
          onFocus={() => setOpen(true)}
          autoComplete="off"
        />
        <div style={{
          position: "absolute",
          right: 10,
          top: "50%",
          transform: "translateY(-50%)",
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}>
          {mocSearchLoading ? (
            <span style={{ display: "flex", alignItems: "center", gap: 3, color: "#004AAD" }}>
              <Loader2 size={13} className="cdp-spin" />
            </span>
          ) : inputVal ? (
            <button
              type="button"
              onClick={handleClear}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
                display: "flex",
                alignItems: "center",
                color: "#94a3b8",
                fontSize: 14,
                lineHeight: 1,
              }}
              aria-label="Clear MOC"
            >
              ✕
            </button>
          ) : (
            <span style={{ color: "#94a3b8", fontSize: 13 }}>▾</span>
          )}
        </div>
      </div>

      {/* Dropdown suggestions */}
      {open && (
        <div style={{
          position: "absolute",
          left: 0,
          right: 0,
          zIndex: 200,
          top: "calc(100% + 4px)",
          background: "#fff",
          borderRadius: 10,
          boxShadow: "0 8px 30px rgba(2,6,23,0.14)",
          border: "1px solid rgba(0,74,173,0.1)",
          maxHeight: 240,
          overflowY: "auto",
          padding: 4,
        }}>
          {hasApiResults ? (
            <>
              <div style={{
                fontSize: 10,
                fontWeight: 700,
                color: "#004AAD",
                textTransform: "uppercase",
                padding: "4px 8px",
                borderBottom: "1px solid #f1f5f9",
                marginBottom: 2
              }}>
                Matching MOC Records ({searchResults.length})
              </div>
              {searchResults.map((item, i) => {
                const mocVal = item.MOCNO || item.MOC_NO || item.MOC || "No MOC";
                return (
                  <div
                    key={item.PO_NO ? `${item.PO_NO}_${i}` : i}
                    onMouseDown={(e) => { e.preventDefault(); handleSelectResult(item); }}
                    style={{
                      padding: "8px 10px",
                      borderRadius: 7,
                      cursor: "pointer",
                      fontSize: 12,
                      color: "#1e293b",
                      transition: "background 0.15s",
                      borderBottom: i < searchResults.length - 1 ? "1px solid #f8fafc" : "none",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(0,74,173,0.07)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                  >
                    <div style={{ fontWeight: 700, color: "#004AAD", display: "flex", justifyContent: "space-between" }}>
                      <span>MOC: {mocVal}</span>
                      {item.PO_NO && <span style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>PO: {item.PO_NO}</span>}
                    </div>
                    {item.SUPPLIER_NAME && (
                      <div style={{ fontSize: 11, color: "#475569", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        Supplier: {item.SUPPLIER_NAME}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          ) : filteredOptions.length > 0 ? (
            filteredOptions.map((opt) => (
              <div
                key={opt}
                onMouseDown={(e) => { e.preventDefault(); handleSelectOption(opt); }}
                style={{
                  padding: "8px 12px",
                  borderRadius: 7,
                  cursor: "pointer",
                  fontSize: 13,
                  color: "#1e293b",
                  fontWeight: opt === value ? 700 : 400,
                  background: opt === value ? "rgba(0,74,173,0.07)" : "transparent",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(0,74,173,0.07)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = opt === value ? "rgba(0,74,173,0.07)" : "transparent"; }}
              >
                {opt}
              </div>
            ))
          ) : (
            <div style={{ padding: "8px 12px", fontSize: 12, color: "#94a3b8" }}>
              No matching options
            </div>
          )}
        </div>
      )}

      {/* Feedback: supplier found via MOC */}
      {/* {!mocSearchLoading && mocSearchResult && value && (
        <div style={{
          marginTop: 5,
          padding: "6px 10px",
          background: "rgba(16,185,129,0.08)",
          border: "1px solid rgba(16,185,129,0.3)",
          borderRadius: 8,
          fontSize: 11,
          color: "#065f46",
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}>
          <CheckCircle2 size={12} color="#10b981" />
          <span>
            <b>Supplier:</b> {mocSearchResult.SUPPLIER_NAME || "-"}
            {mocSearchResult.PO_NO && (
              <span style={{ marginLeft: 8 }}>
                <b>PO:</b> {mocSearchResult.PO_NO}
              </span>
            )}
          </span>
        </div>
      )} */}

      {/* Feedback: not found */}
      {/* {!mocSearchLoading && !mocSearchResult && value && (
        <div style={{
          marginTop: 5,
          padding: "4px 10px",
          background: "rgba(245,158,11,0.08)",
          border: "1px solid rgba(245,158,11,0.25)",
          borderRadius: 8,
          fontSize: 11,
          color: "#92400e",
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}>
          <AlertCircle size={12} color="#f59e0b" />
          No supplier found for this MOC No
        </div>
      )} */}
    </div>
  );
}

function CollectionTableRow({ item, idx, onEdit, getBadgeClasses }) {
  const [copied, setCopied] = useState(false);
  const description = item.description || "";
  const charCount = description.length;

  const handleCopyDescription = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(description);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getTruncatedDescription = (text, maxLength = 60) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  return (
    <tr style={{
      borderBottom: "1px solid #f1f5f9",
      transition: "background 0.2s ease",
      background: item.collected ? "rgba(16,185,129,0.04)" : "transparent",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.background = item.collected 
        ? "rgba(16,185,129,0.08)" 
        : "rgba(0,74,173,0.03)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = item.collected 
        ? "rgba(16,185,129,0.04)" 
        : "transparent";
    }}
    >
      <td style={{ padding: "10px 12px", fontSize: 12, color: "#94a3b8", fontWeight: 500 }}>
        #{idx + 1}
      </td>
      <td style={{ padding: "10px 12px" }}>
  <span
    style={{
      padding: "2px 10px",
      borderRadius: 20,
      fontSize: 11,
      fontWeight: 600,
      display: "inline-block",
      whiteSpace: "nowrap",
      backgroundColor:
        item.status?.toLowerCase() === "pending"
          ? "#FFF3CD"
          : item.status?.toLowerCase() === "collect"
          ? "#c2ebd8"
          : "#E9ECEF",
      color:
        item.status?.toLowerCase() === "pending"
          ? "#856404"
          : item.status?.toLowerCase() === "collect"
          ? "#0F5132"
          : "#495057",
    }}
  >
    {item.status}
  </span>
</td>
      <td style={{ padding: "10px 12px" }}>
        <span style={{ 
          fontSize: 12, 
          fontWeight: 700, 
          color: "#004AAD",
          whiteSpace: "nowrap"
        }}>
          {item.poNo || "-"}
        </span>
      </td>
      <td style={{ padding: "10px 12px" }}>
        <span style={{ 
          fontSize: 12, 
          fontWeight: 700, 
          //color: "#004AAD",
          whiteSpace: "nowrap"
        }}>
          {item.moc || "-"}
        </span>
      </td>
      <td style={{ padding: "10px 12px" }}>
        <div style={{ 
          fontSize: 12,
          color: item.collected ? "#94a3b8" : "#1e293b",
          textDecoration: item.collected ? "line-through" : "none",
          display: "flex",
          alignItems: "center",
          gap: 6,
          maxWidth: "250px",
        }}>
          <span style={{ 
            overflow: "hidden", 
            textOverflow: "ellipsis", 
            whiteSpace: "nowrap",
            flex: 1
          }}>
            {description ? getTruncatedDescription(description, 60) : <span style={{ color: "#cbd5e1", fontStyle: "italic" }}>No description</span>}
          </span>
          {description && (
            <button
              onClick={handleCopyDescription}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "2px 4px",
                fontSize: 11,
                color: copied ? "#10b981" : "#94a3b8",
                flexShrink: 0,
                transition: "color 0.2s ease",
              }}
              aria-label="Copy description"
            >
              <Copy size={12} />
            </button>
          )}
          {description && description.length > 60 && (
            <span style={{ 
              fontSize: 9, 
              color: "#94a3b8", 
              background: "#f1f5f9", 
              padding: "0 4px", 
              borderRadius: 3,
              flexShrink: 0
            }}>
              {charCount}
            </span>
          )}
        </div>
      </td>
      <td style={{ padding: "10px 12px" }}>
        <div style={{ 
          fontSize: 12,
          color: item.collected ? "#94a3b8" : "#1e293b",
          maxWidth: "200px",
        }}>
          <span style={{ 
            overflow: "hidden", 
            textOverflow: "ellipsis", 
            whiteSpace: "nowrap",
            display: "block"
          }}>
            {item.momDescription || <span style={{ color: "#cbd5e1", fontStyle: "italic" }}>-</span>}
          </span>
        </div>
      </td>
      <td style={{ padding: "10px 12px" }}>
        <span style={{ 
          fontSize: 12, 
          color: "#64748b",
          whiteSpace: "nowrap"
        }}>
          {item.momQuantity || "-"}
        </span>
      </td>
      <td style={{ padding: "10px 12px" }}>
        <div style={{ 
          fontSize: 12, 
          color: "#64748b",
          display: "flex",
          alignItems: "flex-start",
          gap: 4,
          maxWidth: "200px",
        }}>
          <Truck size={11} color="#94a3b8" style={{ flexShrink: 0, marginTop: 1 }} />
          <span style={{ 
            overflow: "hidden", 
            textOverflow: "ellipsis", 
            whiteSpace: "nowrap" 
          }}>
            {item.suppliername || "-"}
          </span>
        </div>
      </td>
      <td style={{ padding: "10px 12px" }}>
        <span style={{ 
          fontSize: 12, 
          color: "#64748b",
          whiteSpace: "nowrap"
        }}>
          {item.HANDLE_BY || "-"}
        </span>
      </td>
      <td style={{ padding: "10px 12px" }}>
        <span style={{ 
          fontSize: 12, 
          color: "#64748b",
          whiteSpace: "nowrap"
        }}>
          {item.endUser || "-"}
        </span>
      </td>
      <td style={{ padding: "10px 12px" }}>
        <span style={{ 
          fontSize: 12, 
          color: "#64748b",
          whiteSpace: "nowrap"
        }}>
          {item.remark || "-"}
        </span>
      </td>
      <td style={{ padding: "10px 12px" }}>
        <span style={{ 
          fontSize: 12, 
          color: "#64748b",
          whiteSpace: "nowrap",
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
        }}>
          <MapPin size={11} color="#94a3b8" />
          {item.areaId ? getAreaLabel(item.areaId) : "-"}
        </span>
      </td>
      <td style={{ padding: "10px 12px", textAlign: "center" }}>
        <button
          onClick={() => onEdit(item)}
          style={{
            background: "rgba(0,74,173,0.08)",
            border: "none",
            cursor: "pointer",
            padding: "4px 10px",
            borderRadius: 6,
            color: "#004AAD",
            fontSize: 12,
            fontWeight: 500,
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            transition: "all 0.2s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(0,74,173,0.15)";
            e.currentTarget.style.transform = "scale(1.05)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(0,74,173,0.08)";
            e.currentTarget.style.transform = "scale(1)";
          }}
          aria-label={`Edit item ${idx + 1}`}
        >
          <Edit size={13} />
          Edit
        </button>
      </td>
    </tr>
  );
}

function CollectionCard({ item, idx, onEdit, getBadgeClasses }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const description = item.description || "";
  const isLong = description.length > 100;
  const charCount = description.length;

  const handleCopyDescription = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(description);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getTruncatedDescription = (text, maxLength = 80) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 16,
        boxShadow: "0 2px 12px rgba(0,74,173,0.07)",
        border: item.collected
          ? "1px solid #86efac"
          : "1px solid rgba(0,74,173,0.08)",
        overflow: "hidden",
        transition: "all 0.2s ease",
      }}
    >
      <div
        onClick={() => setOpen((prev) => !prev)}
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 8,
          padding: "10px 14px",
          cursor: "pointer",
          background: item.collected
            ? "rgba(16,185,129,0.06)"
            : "rgba(0,74,173,0.03)",
          userSelect: "none",
          transition: "background 0.2s ease",
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              marginBottom: 4,
              flexWrap: "wrap",
            }}
          >
            <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600, flexShrink: 0 }}>
              #{idx + 1}
            </span>
            <span
              className={getBadgeClasses(item.status)}
              style={{ padding: "1px 8px", borderRadius: 20, fontSize: 11, fontWeight: 600, flexShrink: 0 }}
            >
              {item.status}
            </span>
            {item.poNo && (
              <span style={{ fontSize: 11, fontWeight: 700, color: "#004AAD", flexShrink: 0 }}>
                {item.poNo}
              </span>
            )}
            {item.areaId && (
              <span style={{ 
                fontSize: 10, 
                fontWeight: 600, 
                color: "#4338ca",
                background: "#e0e7ff",
                padding: "1px 10px",
                borderRadius: 12,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                gap: 3,
              }}>
                <MapPin size={10} />
                {getAreaLabel(item.areaId)}
              </span>
            )}
          </div>

          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: item.collected ? "#94a3b8" : "#1e293b",
              textDecoration: item.collected ? "line-through" : "none",
              lineHeight: 1.5,
              marginBottom: 4,
              wordBreak: "break-word",
              padding: "6px 10px",
              background: "rgba(0,74,173,0.04)",
              borderRadius: 6,
              borderLeft: "3px solid #004AAD",
              position: "relative",
            }}
          >
            {description ? (
              <>
                {getTruncatedDescription(description, 100)}
                {isLong && (
                  <span
                    style={{
                      fontSize: 10,
                      color: "#94a3b8",
                      marginLeft: 8,
                      background: "#f1f5f9",
                      padding: "0 6px",
                      borderRadius: 4,
                      fontWeight: 400,
                    }}
                  >
                    {charCount} chars
                  </span>
                )}
                <button
                  onClick={handleCopyDescription}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "2px 6px",
                    fontSize: 11,
                    color: copied ? "#10b981" : "#94a3b8",
                    marginLeft: 6,
                    transition: "color 0.2s ease",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                  aria-label="Copy description"
                >
                  <Copy size={12} />
                  {copied && (
                    <span style={{ fontSize: 10, color: "#10b981" }}>
                      Copied!
                    </span>
                  )}
                </button>
              </>
            ) : (
              <span style={{ color: "#cbd5e1", fontStyle: "italic", fontWeight: 400 }}>
                No description
              </span>
            )}
          </div>

          {item.suppliername && (
            <div
              style={{
                fontSize: 12,
                color: "#64748b",
                display: "flex",
                alignItems: "flex-start",
                gap: 4,
                lineHeight: 1.4,
                wordBreak: "break-word",
              }}
            >
              <Truck size={11} color="#94a3b8" style={{ flexShrink: 0, marginTop: 2 }} />
              {item.suppliername}
            </div>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0, marginTop: 4 }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit(item);
            }}
            style={{ background: "none", border: "none", cursor: "pointer", padding: "2px 4px" }}
            aria-label={`Edit item ${idx + 1}`}
          >
            <Edit size={15} color="#004AAD" />
          </button>
          <ChevronDown
            size={15}
            color="#94a3b8"
            style={{
              transition: "transform 0.2s",
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
            }}
          />
        </div>
      </div>

      {open && (
        <div
          style={{
            padding: "12px 16px",
            borderTop: "1px solid rgba(0,74,173,0.06)",
          }}
        >
          <p
            style={{
              fontWeight: 600,
              fontSize: 14,
              textDecoration: item.collected ? "line-through" : "none",
              color: item.collected ? "#94a3b8" : "#1e293b",
              marginBottom: 8,
              display: "flex",
              alignItems: "flex-start",
              gap: 6,
              padding: "8px 12px",
              background: "rgba(0,74,173,0.04)",
              borderRadius: 6,
              borderLeft: "3px solid #004AAD",
            }}
          >
            <FileText size={14} color="#64748b" style={{ flexShrink: 0, marginTop: 2 }} />
            <span style={{ flex: 1, wordBreak: "break-word" }}>
              {description || (
                <span style={{ color: "#cbd5e1", fontStyle: "italic" }}>
                  No description
                </span>
              )}
              {description && (
                <span
                  style={{
                    fontSize: 11,
                    color: "#94a3b8",
                    marginLeft: 8,
                    fontWeight: 400,
                  }}
                >
                  ({charCount} characters)
                </span>
              )}
            </span>
            {description && (
              <button
                onClick={handleCopyDescription}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "2px 6px",
                  fontSize: 11,
                  color: copied ? "#10b981" : "#94a3b8",
                  flexShrink: 0,
                }}
              >
                <Copy size={14} />
              </button>
            )}
          </p>

          <div
            style={{
              marginBottom: 8,
              fontSize: 11,
              color: "#0f172a",
              background: "#eef2ff",
              padding: "4px 8px",
              borderRadius: 6,
            }}
          >
            <b>Chaser Remark:</b>{" "}
            {item.remark && item.remark.trim()
              ? item.remark
              : "No remark from chaser"}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "4px 16px",
              fontSize: 12,
              color: "#64748b",
            }}
          >
            {item.HANDLE_BY && (
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <Users size={10} /> <b>Handle By:</b> {item.HANDLE_BY}
              </span>
            )}
            {item.endUser && (
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <User size={10} /> <b>Request By:</b> {item.endUser}
              </span>
            )}
            {item.areaId && (
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <MapPin size={10} /> <b>Area:</b> {getAreaLabel(item.areaId)}
              </span>
            )}
            {item.moc && (
              <span>
                <span
                  style={{
                    background: "rgba(0,74,173,0.08)",
                    color: "#004AAD",
                    fontWeight: 700,
                    padding: "1px 6px",
                    borderRadius: 4,
                    fontSize: 11,
                  }}
                >
                  {item.moc}
                </span>
              </span>
            )}
            {item.End_User_By && (
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <FileText size={10} /> <b>End User By:</b> {item.End_User_By}
              </span>
            )}
            
            {item.End_User && (
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <FileText size={10} /> <b>Chaser:</b> {item.End_User}
              </span>
            )}
            {item.jobNo && (
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <FileText size={10} /> <b>Job No:</b> {item.jobNo}
              </span>
            )}
            {item.poNo && (
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <Package size={10} /> <b>PO No:</b> {item.poNo}
              </span>
            )}
            {item.serialNo && (
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <b>SN:</b> {item.serialNo}
              </span>
            )}
            {item.QUANTITY && (
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <FileText size={10} /> <b>Quantity:</b> {item.QUANTITY}
              </span>
            )}
            {item.ITEM_DESCRIPTION && (
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <FileText size={10} /> <b>Material Description:</b> {item.ITEM_DESCRIPTION}
              </span>
            )}
            {item.supplierCode && (
              <span
                style={{
                  gridColumn: "1 / -1",
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <Truck size={10} /> <b>Supplier Code:</b> {item.supplierCode}
              </span>
            )}
            {item.suppliername && (
              <span
                style={{
                  gridColumn: "1 / -1",
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <Truck size={10} /> <b>Supplier Name:</b> {item.suppliername}
              </span>
            )}
            {item.SupplierAddress && (
              <span
                style={{
                  gridColumn: "1 / -1",
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <Truck size={10} /> <b>Supplier Address:</b>{" "}
                {item.SupplierAddress}
              </span>
            )}
            {item.Sup_Contact && (
              <span
                style={{
                  gridColumn: "1 / -1",
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <Truck size={10} /> <b>Contact No:</b> {item.Sup_Contact}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminCollectionPage() {
  const [view, setView] = useState("list");
  const [items, setItems] = useState([]);
  const itemsRef = useRef([]);
  const [form, setForm] = useState(defaultForm);
  const [editId, setEditId] = useState(null);
  const [toast, setToast] = useState(null);

  const [selectedAdmin, setSelectedAdmin] = useState("");
  const [selectedChaser, setSelectedChaser] = useState("");
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [filteredItems, setFilteredItems] = useState([]);
 
  const [searchTerm, setSearchTerm] = useState("");

  const [poOptions, setPoOptions] = useState([]);
  const [mocOptions, setMocOptions] = useState([]);
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [apiData, setApiData] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [poSearchLoading, setPoSearchLoading] = useState(false);
  const [poSearchResult, setPoSearchResult] = useState(null);
  const [poSearchResultsList, setPoSearchResultsList] = useState([]);

  const [mocSearchLoading, setMocSearchLoading] = useState(false);
  const [mocSearchResult, setMocSearchResult] = useState(null);
  const [mocSearchResultsList, setMocSearchResultsList] = useState([]);

  const isProgrammaticPoChangeRef = useRef(false);
  const isProgrammaticMocChangeRef = useRef(false);

  const [descriptionError, setDescriptionError] = useState("");
  const MAX_DESCRIPTION_LENGTH = 1000;

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);
 
  useEffect(() => {
    let filtered = items;
 
    if (selectedAdmin) {
      const adminServiceNo = extractServiceNo(selectedAdmin);
      filtered = filtered.filter(
        (item) =>
          item.HANDLE_BY === adminServiceNo ||
          item.handlingAdmin === selectedAdmin ||
          extractServiceNo(item.handlingAdmin) === adminServiceNo
      );
    }

    if (selectedChaser) {
      const chaserServiceNo = extractServiceNo(selectedChaser);
      filtered = filtered.filter(
        (item) =>
          item.endUserServiceNo === chaserServiceNo ||
          item.CHASER_ID === chaserServiceNo ||
          item.collectedByChaser === chaserServiceNo
      );
    }

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(
        (item) =>
          (item.description || "").toLowerCase().includes(searchLower) ||
          (item.poNo || "").toLowerCase().includes(searchLower) ||
          (item.suppliername || "").toLowerCase().includes(searchLower) ||
          (item.jobNo || "").toLowerCase().includes(searchLower)
      );
    }

    filtered = [...filtered].sort((a, b) => {
      if (a.collected === b.collected) return 0;
      return a.collected ? 1 : -1;
    });

    setFilteredItems(filtered);
  }, [items, selectedAdmin, selectedChaser, searchTerm]);

  useEffect(() => {
    const fetchToDoList = async () => {
      setLoadingOptions(true);
      try {
        const response = await CommonService.GetToDoList();
        if (response.data && response.data.ResultSet) {
          const data = response.data.ResultSet;
          setApiData(data);

          const processedData = data.map((item) => ({
            ...item,
            momDescription: item.MOM_DESCRIPTION || "",
            momQuantity: item.MOM_QUANTITY || "",
            momUnitPrice: item.MOM_UNIT_PRICE || ""
          }));
          setApiData(processedData);

          const uniquePoNos = [
            ...new Set(data.map((item) => item.PO_NO).filter(Boolean)),
          ];
          setPoOptions(uniquePoNos);

          const uniqueMocNos = [
            ...new Set(
              data.map((item) => item.MOCNO || item.MOC_NO || item.MOC).filter(Boolean)
            ),
          ];
          setMocOptions(uniqueMocNos);

          const uniqueSuppliers = [
            ...new Set(
              data.map((item) => item.SUPPLIER_NAME).filter(Boolean)
            ),
          ];
          setSupplierOptions(uniqueSuppliers);
        }
      } catch (error) {
        console.error("Error fetching TODO list:", error);
        showToast("Failed to load options from API", "error");
      } finally {
        setLoadingOptions(false);
      }
    };

    fetchToDoList();
  }, []);

  // Fetch supplier/moc details when a specific PO number is entered
  const getMomFields = (item) => ({
    momQuantity: item.MOM_QUANTITY || "",
    momDescription: item.MOM_DESCRIPTION || "",
    momUnitPrice: item.MOM_UNIT_PRICE || "",
  });

  const fetchSupplierByPoNo = async (poNo) => {
    if (!poNo || !poNo.trim()) {
      setPoSearchResult(null);
      setPoSearchResultsList([]);
      return;
    }
    setPoSearchLoading(true);
    try {
      const response = await CommonService.GetToDoList({ P_SEARCH_PONO: poNo.trim() });
      if (response.data && response.data.ResultSet && response.data.ResultSet.length > 0) {
        const results = response.data.ResultSet;
        setPoSearchResultsList(results);
        const firstItem = results[0];
        setPoSearchResult(firstItem);

        const mocValue =
          firstItem.MOCNO ??
          firstItem.MOC_NO ??
          firstItem.MOCNo ??
          firstItem.MOC_no ??
          firstItem.MOC ??
          firstItem.moc ??
          null;
        const normalizedMoc =
          mocValue && String(mocValue).toLowerCase() !== "null"
            ? String(mocValue)
            : "";

        const supplierCodeVal =
          firstItem.SUPPLIER_CODE ||
          firstItem.SUPPLIER_CD ||
          firstItem.SupplierCode ||
          firstItem.SUP_CODE ||
          "";

        isProgrammaticMocChangeRef.current = true;
        setForm((prev) => ({
          ...prev,
          supplierName: firstItem.SUPPLIER_NAME || prev.supplierName,
          supplierCode: supplierCodeVal || prev.supplierCode,
          moc: normalizedMoc || prev.moc,
          ...getMomFields(firstItem),
          jobNo:
            firstItem.JCAT && firstItem.JMAIN
              ? `${firstItem.JCAT}${firstItem.JMAIN}`
              : prev.jobNo,
        }));

        const letter = extractLetterFromPoNo(poNo);
        if (letter) {
          const admin = getAdminByLetter(letter);
          if (admin) {
            const adminValue = `${admin.serviceNo} - ${admin.name}`;
            setSelectedAdmin(adminValue);
            setForm((prev) => ({ ...prev, handlingAdmin: adminValue }));
          }
        }
      } else {
        setPoSearchResult(null);
        setPoSearchResultsList([]);
        const letter = extractLetterFromPoNo(poNo);
        if (letter) {
          const admin = getAdminByLetter(letter);
          if (admin) {
            const adminValue = `${admin.serviceNo} - ${admin.name}`;
            setSelectedAdmin(adminValue);
            setForm((prev) => ({ ...prev, handlingAdmin: adminValue }));
          }
        }
      }
    } catch (error) {
      console.error("Error fetching supplier by PO No:", error);
      setPoSearchResult(null);
      setPoSearchResultsList([]);
    } finally {
      setPoSearchLoading(false);
    }
  };

  // Fetch supplier/po details when a specific MOC number is entered
  const fetchSupplierByMocNo = async (mocNo) => {
    if (!mocNo || !mocNo.trim()) {
      setMocSearchResult(null);
      setMocSearchResultsList([]);
      return;
    }
    setMocSearchLoading(true);
    try {
      const response = await CommonService.GetToDoList({ P_SEARCH_MOCNO: mocNo.trim() });
      if (response.data && response.data.ResultSet && response.data.ResultSet.length > 0) {
        const results = response.data.ResultSet;
        setMocSearchResultsList(results);
        const firstItem = results[0];
        setMocSearchResult(firstItem);

        const supplierCodeVal =
          firstItem.SUPPLIER_CODE ||
          firstItem.SUPPLIER_CD ||
          firstItem.SupplierCode ||
          firstItem.SUP_CODE ||
          "";

        // Flag that poNo is being changed programmatically by MOC search so PO search is NOT triggered
        isProgrammaticPoChangeRef.current = true;

        setForm((prev) => ({
          ...prev,
          supplierName: firstItem.SUPPLIER_NAME || prev.supplierName,
          supplierCode: supplierCodeVal || prev.supplierCode,
          poNo: firstItem.PO_NO || prev.poNo,
          ...getMomFields(firstItem),
          jobNo:
            firstItem.JCAT && firstItem.JMAIN
              ? `${firstItem.JCAT}${firstItem.JMAIN}`
              : prev.jobNo,
        }));

        // Auto-set admin from PO letter
        if (firstItem.PO_NO) {
          const letter = extractLetterFromPoNo(firstItem.PO_NO);
          if (letter) {
            const admin = getAdminByLetter(letter);
            if (admin) {
              const adminValue = `${admin.serviceNo} - ${admin.name}`;
              setSelectedAdmin(adminValue);
              setForm((prev) => ({ ...prev, handlingAdmin: adminValue }));
            }
          }
        }
      } else {
        setMocSearchResult(null);
        setMocSearchResultsList([]);
      }
    } catch (error) {
      console.error("Error fetching supplier by MOC No:", error);
      setMocSearchResult(null);
      setMocSearchResultsList([]);
    } finally {
      setMocSearchLoading(false);
    }
  };

  const handleSelectPoResultItem = (item) => {
    setPoSearchResult(item);
    const mocValue =
      item.MOCNO ??
      item.MOC_NO ??
      item.MOCNo ??
      item.MOC_no ??
      item.MOC ??
      item.moc ??
      null;
    const normalizedMoc =
      mocValue && String(mocValue).toLowerCase() !== "null"
        ? String(mocValue)
        : "";

    const supplierCodeVal =
      item.SUPPLIER_CODE ||
      item.SUPPLIER_CD ||
      item.SupplierCode ||
      item.SUP_CODE ||
      "";

    isProgrammaticPoChangeRef.current = true;
    isProgrammaticMocChangeRef.current = true;

    setForm((prev) => ({
      ...prev,
      poNo: item.PO_NO || prev.poNo,
      supplierName: item.SUPPLIER_NAME || prev.supplierName,
      supplierCode: supplierCodeVal || prev.supplierCode,
      moc: normalizedMoc || prev.moc,
      ...getMomFields(item),
      jobNo:
        item.JCAT && item.JMAIN
          ? `${item.JCAT}${item.JMAIN}`
          : prev.jobNo,
    }));

    if (item.PO_NO) {
      const letter = extractLetterFromPoNo(item.PO_NO);
      if (letter) {
        const admin = getAdminByLetter(letter);
        if (admin) {
          const adminValue = `${admin.serviceNo} - ${admin.name}`;
          setSelectedAdmin(adminValue);
          setForm((prev) => ({ ...prev, handlingAdmin: adminValue }));
        }
      }
    }
  };

  const handleSelectMocResultItem = (item) => {
    setMocSearchResult(item);
    const mocValue =
      item.MOCNO ??
      item.MOC_NO ??
      item.MOCNo ??
      item.MOC_no ??
      item.MOC ??
      item.moc ??
      null;
    const normalizedMoc =
      mocValue && String(mocValue).toLowerCase() !== "null"
        ? String(mocValue)
        : "";

    const supplierCodeVal =
      item.SUPPLIER_CODE ||
      item.SUPPLIER_CD ||
      item.SupplierCode ||
      item.SUP_CODE ||
      "";

    isProgrammaticPoChangeRef.current = true;
    isProgrammaticMocChangeRef.current = true;

    setForm((prev) => ({
      ...prev,
      moc: normalizedMoc || prev.moc,
      poNo: item.PO_NO || prev.poNo,
      supplierName: item.SUPPLIER_NAME || prev.supplierName,
      supplierCode: supplierCodeVal || prev.supplierCode,
      jobNo:
        item.JCAT && item.JMAIN
          ? `${item.JCAT}${item.JMAIN}`
          : prev.jobNo,
    }));

    if (item.PO_NO) {
      const letter = extractLetterFromPoNo(item.PO_NO);
      if (letter) {
        const admin = getAdminByLetter(letter);
        if (admin) {
          const adminValue = `${admin.serviceNo} - ${admin.name}`;
          setSelectedAdmin(adminValue);
          setForm((prev) => ({ ...prev, handlingAdmin: adminValue }));
        }
      }
    }
  };

  const fetchDailyCollect = async () => {
    setLoadingOptions(true);
    try {
      const resp = await CommonService.GetDailyCollect({
        date: formatDateForApi(selectedDate),
      });
      const data =
        resp?.data?.ResultSet || resp?.data?.Result || [];
      if (Array.isArray(data) && data.length > 0) {
        const existingBySerial = new Map(
          itemsRef.current.map((it) => [String(it.serialNo ?? ""), it])
        );

        const mapped = data.map((r, idx) => {
          const id = `${(r.DATE || "")
            .toString()
            .replace(/\s+/g, "_")}_${r.SERIAL_NO ?? idx}_${r.PO_NO ?? ""}`;

          let dateIso = selectedDate;
          try {
            const parsed = new Date(r.DATE);
            if (!Number.isNaN(parsed.getTime()))
              dateIso = parsed.toISOString();
          } catch (e) {}

          const normalizedStatus = normalizeStatusFromApi(r.STATUS);
          const existingItem = existingBySerial.get(
            String(r.SERIAL_NO ?? "")
          );
          const serverRemark = r.CHASER_REMARK || r.REMARK || "";
          const remark = serverRemark || existingItem?.remark || "";

          return {
            id,
            serialNo: r.SERIAL_NO ?? null,
            HANDLE_BY: r.HANDLE_BY || "",
            CHASER_ID: r.CHASER_ID || "",
            handlingAdmin: r.HANDLE_BY || r.HANDLED_BY || "",
            endUser: r.REQUEST_BY || "",
            endUserServiceNo: r.REQUEST_BY || "",
            moc: normalizeMocValue(
              r.MOC_NO ?? r.MOCNO ?? r.MOC ?? r.MOCNo ?? r.MOC_no
            ),
            jobNo: (r.JCAT || "") + (r.JMAIN || ""),
            description: r.DESCRIPTION || "",
            poNo: r.PO_NO || r.PO || "",
            End_User_By: r.End_User_By,
            ITEM_DESCRIPTION: r.ITEM_DESCRIPTION || "",
            QUANTITY: r.QUANTITY,
            End_User: r.End_User,
            supplierCode: r.SUPPLIER_NAME || r.SUPPLIER_CODE || "",
            suppliername: r.Sup_Name,
            SupplierAddress: r.Sup_Address,
            Sup_Contact: r.Sup_Contact,
            pcNo: r.PC_NO || r.PCNo || r.PC || "",
            status: normalizedStatus,
            collected: normalizedStatus === "Collected",
            collectedByChaser: r.INVCOLLECTED_BY || "",
            remark,
            date: dateIso,
            momDescription: r.MOM_DESCRIPTION || r.ITEM_DESCRIPTION || "",
            momQuantity: r.MOM_QUANTITY || r.QUANTITY || "",
            momUnitPrice: r.MOM_UNIT_PRICE || r.UNIT_PRICE || "",
            areaId: r.MDD_AREA_ID || "", 
          };
        });
        setItems(mapped);
      } else {
        setItems([]);
      }
    } catch (err) {
      console.error("Error fetching DailyCollect:", err);
      showToast("Failed to load collection list from server", "error");
    } finally {
      setLoadingOptions(false);
    }
  };

  useEffect(() => {
    fetchDailyCollect();
  }, [selectedDate]);

  // When poNo changes in the form, trigger live API search for supplier
  const poSearchDebounceRef = useRef(null);
  useEffect(() => {
    if (isProgrammaticPoChangeRef.current) {
      isProgrammaticPoChangeRef.current = false;
      return;
    }
    if (!form.poNo) {
      setPoSearchResult(null);
      setPoSearchResultsList([]);
      return;
    }
    if (poSearchDebounceRef.current) clearTimeout(poSearchDebounceRef.current);
    poSearchDebounceRef.current = setTimeout(() => {
      fetchSupplierByPoNo(form.poNo);
    }, 400);
    return () => {
      if (poSearchDebounceRef.current) clearTimeout(poSearchDebounceRef.current);
    };
  }, [form.poNo]);

  // When moc changes in the form, trigger live API search for supplier
  const mocSearchDebounceRef = useRef(null);
  useEffect(() => {
    if (isProgrammaticMocChangeRef.current) {
      isProgrammaticMocChangeRef.current = false;
      return;
    }
    if (!form.moc) {
      setMocSearchResult(null);
      setMocSearchResultsList([]);
      return;
    }
    if (mocSearchDebounceRef.current) clearTimeout(mocSearchDebounceRef.current);
    mocSearchDebounceRef.current = setTimeout(() => {
      fetchSupplierByMocNo(form.moc);
    }, 400);
    return () => {
      if (mocSearchDebounceRef.current) clearTimeout(mocSearchDebounceRef.current);
    };
  }, [form.moc]);

  function showToast(msg, type = "success") {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  }

  async function handleSubmit() {
    let existingRemark = "";
    if (editId) {
      const existingItem = items.find((it) => it.id === editId);
      if (existingItem && existingItem.remark)
        existingRemark = existingItem.remark;
    }
 
    const newItem = {
      ...form,
      id: editId || generateId(),
      collected: false,
      date: selectedDate,
      handlingAdmin: selectedAdmin,
      remark: form.remark || existingRemark,
    };

    const matchingItem =
      apiData.find((it) => it.PO_NO === form.poNo) || {};
    const existingItem = editId
      ? items.find((it) => it.id === editId)
      : null;
    const serialNo = editId
      ? form.serialNo ?? existingItem?.serialNo ?? null
      : null;

    if (editId && !serialNo) {
      showToast("Serial number is required to update.", "error");
      return;
    }

    const { jobCat, jobMain } = splitJobNoValue(
      form.jobNo,
      matchingItem.JCAT || "",
      matchingItem.JMAIN || ""
    );
    const payloadDate = editId
      ? form.date || selectedDate
      : selectedDate;
    const adminServiceNo = extractServiceNo(selectedAdmin);
    const chaserServiceNo = extractServiceNo(selectedChaser);

    const payload = {
      P_MDD_DATE: formatDateForApi(payloadDate),
      P_MDD_CHASER_ID: chaserServiceNo || "1",
      P_MDD_HANDLE_BY: adminServiceNo || form.handlingAdmin || "",
      P_MDD_REQUEST_BY: form.endUserServiceNo || form.endUser || "",
      P_MDD_MOC_NO:
        normalizeMocValue(form.moc) ||
        normalizeMocValue(existingItem?.moc) ||
        normalizeMocValue(matchingItem.MOCNO) ||
        normalizeMocValue(matchingItem.MOC_NO),
      P_MDD_JCAT: jobCat,
      P_MDD_JMAIN: jobMain,
      P_MDD_DESCRIPTION: form.description || "",
     // P_MDD_MOM_QUANTITY: form.momQuantity || "",
     // P_MDD_MOM_DESCRIPTION: form.momDescription || "",
     // P_MDD_MOM_UNIT_PRICE: form.momUnitPrice || "",
      P_MDD_PO_NO: form.poNo || "",
      P_MDD_SUPPLIER_CODE:
        matchingItem.SUPPLIER_CODE ||
        matchingItem.SUPPLIER_CD ||
        matchingItem.SupplierCode ||
        matchingItem.SUP_CODE ||
        form.supplierCode ||
        existingItem?.supplierCode ||
        "",
      P_MDD_CHASER_REMARK: form.remark || "",
      P_MDD_STATUS: mapStatusForApi(form.status),
      P_MDD_PC_NO: form.pcNo || "",
      P_MDD_INVCOLLECTED_BY: form.collectedByChaser || "",
      P_MDD_AREA_NO: form.area || "",
    };

    if (editId && serialNo) {
      payload.P_MDD_SERIAL_NO = serialNo;
    }

    let apiOk = false;
    let successMsg = "";
    setIsSubmitting(true);
    try {
      const resp = editId
        ? await CommonService.UpdateDailyCollect(payload)
        : await CommonService.PostDailyCollect(payload);

      const bodyStatus = resp?.data?.StatusCode ?? resp?.data?.statusCode;
      const bodyResult = resp?.data?.Result ?? resp?.data?.result;
      const serverMsg =
        resp?.data?.Message ??
        resp?.data?.message ??
        resp?.data?.resultMessage ??
        (typeof bodyResult === "string" ? bodyResult : null);

      if (bodyStatus === 200) {
        apiOk = true;
        successMsg = serverMsg || "";
      } else {
        console.warn("Server returned error:", resp?.data);
        showToast(
          serverMsg || `Server error (code ${bodyStatus ?? "unknown"})`,
          "error"
        );
      }
    } catch (err) {
      const serverMsg =
        err?.response?.data?.Message ??
        err?.response?.data?.message ??
        err?.response?.data?.Result ??
        err.message ??
        "Network error";
      console.error("PostDailyCollect failed:", err);
      showToast(serverMsg || "Failed to save to server.", "error");
    } finally {
      setIsSubmitting(false);
    }

    if (!apiOk) return;

    if (editId) {
      setItems((prev) =>
        prev.map((it) => (it.id === editId ? newItem : it))
      );
      showToast(successMsg || "Item updated successfully!");
    } else {
      setItems((prev) => [...prev, newItem]);
      showToast(successMsg || "Item added successfully!");
    }

    fetchDailyCollect();
    setForm(defaultForm);
    setEditId(null);
    setSelectedAdmin("");
    setView("list");
    setSearchTerm("");
  }

  function handleEdit(item) {
    setForm({ 
      ...item, 
      area: item.areaId || item.area || "", 
    });
    setEditId(item.id);
    setSelectedAdmin(item.handlingAdmin);
    setView("form");
    setDescriptionError("");
    setPoSearchResult(null);
    setPoSearchResultsList([]);
    setMocSearchResult(null);
    setMocSearchResultsList([]);
  }

  function handleNew() {
    setForm(defaultForm);
    setEditId(null);
    setSelectedAdmin("");
    setView("form");
    setDescriptionError("");
    setPoSearchResult(null);
    setPoSearchResultsList([]);
    setMocSearchResult(null);
    setMocSearchResultsList([]);
  }

  // ---- Excel export (new) ----
  // function handleExportExcel() {
  //   try {
  //     if (!filteredItems || filteredItems.length === 0) {
  //       showToast("No data to export", "error");
  //       return;
  //     }

  //     const exportRows = filteredItems.map((item, idx) => ({
  //       "#": idx + 1,
  //       Status: item.status || "",
  //       "PO No": item.poNo || "",
  //       Description: item.description || "",
  //       "Item Description": item.momDescription || "",
  //       Quantity: item.momQuantity || "",
  //       Supplier: item.suppliername || "",
  //       "Handle By": item.HANDLE_BY || "",
  //       "Request By": item.endUser || "",
  //       Area: item.areaId ? getAreaLabel(item.areaId) : "",
  //       "Job No": item.jobNo || "",
  //       MOC: item.moc || "",
  //       "Chaser Remark": item.remark || "",
  //       Date: item.date ? new Date(item.date).toLocaleDateString() : "",
  //     }));

  //     const worksheet = XLSX.utils.json_to_sheet(exportRows);
  //     worksheet["!cols"] = [
  //       { wch: 5 },
  //       { wch: 12 },
  //       { wch: 14 },
  //       { wch: 40 },
  //       { wch: 30 },
  //       { wch: 10 },
  //       { wch: 25 },
  //       { wch: 14 },
  //       { wch: 14 },
  //       { wch: 14 },
  //       { wch: 12 },
  //       { wch: 10 },
  //       { wch: 30 },
  //       { wch: 14 },
  //     ];

  //     const workbook = XLSX.utils.book_new();
  //     XLSX.utils.book_append_sheet(workbook, worksheet, "Collection List");

  //     const dateStr = selectedDate || new Date().toISOString().split("T")[0];
  //     XLSX.writeFile(workbook, `Daily_Collection_${dateStr}.xlsx`);

  //     showToast("Excel file downloaded successfully!");
  //   } catch (error) {
  //     console.error("Error exporting to Excel:", error);
  //     showToast("Failed to export Excel file", "error");
  //   }
  // }

  function handleExportExcel() {
  try {
    if (!filteredItems || filteredItems.length === 0) {
      showToast("No data to export", "error");
      return;
    }

    const headers = [
      "#",
      "Status",
      "PO No",
      "Description",
      "Item Description",
      "Quantity",
      "Supplier",
      "Handlin Admin",
      "Chaser",
      "Area",
      "Job No",
      "MOC",
      "Chaser Remark",
    ];

    const rows = filteredItems.map((item, idx) => [
      idx + 1,
      item.status || "",
      item.poNo || "",
      item.description || "",
      item.momDescription || "",
      item.momQuantity || "",
      item.suppliername || "",
      item.HANDLE_BY || "",
      item.endUser || "",
      item.areaId ? getAreaLabel(item.areaId) : "",
      item.jobNo || "",
      item.moc || "",
      item.remark || "",
    ]);

    const csvData = [headers, ...rows];

    const csv = csvData
      .map((row) =>
        row
          .map((value) => {
            const text = String(value ?? "");
            return `"${text.replace(/"/g, '""')}"`;
          })
          .join(",")
      )
      .join("\r\n");

    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;

    const dateStr =
      selectedDate || new Date().toISOString().split("T")[0];

    link.download = `Daily_Collection_${dateStr}.csv`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);

    showToast("CSV file downloaded successfully!");
  } catch (error) {
    console.error("Error exporting to CSV:", error);
    showToast("Failed to export CSV file", "error");
  }
}
  const collected = filteredItems.filter((i) => i.collected).length;
  const pending = filteredItems.filter((i) => !i.collected).length;
  const total = filteredItems.length;

  const adminDisplayValue = selectedAdmin
    ? ADMIN_OPTIONS.find(
        (a) =>
          `${a.serviceNo} - ${a.name}` === selectedAdmin ||
          a.serviceNo === extractServiceNo(selectedAdmin)
      )
      ? selectedAdmin
      : selectedAdmin
    : "";

  const chaserDisplayValue = selectedChaser
    ? typeof selectedChaser === "object"
      ? `${selectedChaser.serviceNo} `
      : selectedChaser
    : "";

  return (
    <div
      className="cdp-dot-bg"
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        overflow: "hidden",
      }}
    >
      <InjectStyles />

    {toast && (
  <div
    className="cdp-toast-in"
    style={{
      position: "fixed",
      top: 20,
      right: 20,
      zIndex: 9999,
      background: toast.type === "error"
        ? "linear-gradient(135deg, #dc2626, #b91c1c)"
        : "linear-gradient(135deg, #10b981, #059669)",
      color: "#fff",
      padding: "16px 24px",
      borderRadius: 12,
      boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
      fontSize: 14,
      fontWeight: 500,
      display: "flex",
      alignItems: "center",
      gap: 12,
      maxWidth: "90%",
      minWidth: "300px",
      maxHeight: "200px",
      overflow: "auto",
      wordBreak: "break-word",
      border: toast.type === "error" ? "2px solid #fca5a5" : "none",
      animation: "slideIn 0.3s ease-out",
    }}
  >
    {toast.type === "error" ? (
      <XCircle size={22} style={{ flexShrink: 0 }} />
    ) : (
      <CheckCircle2 size={22} style={{ flexShrink: 0 }} />
    )}
    <div style={{ flex: 1, lineHeight: 1.5 }}>
      {toast.msg}
    </div>
    {toast.persistent && (
      <button
        onClick={() => setToast(null)}
        style={{
          background: "rgba(255,255,255,0.2)",
          border: "none",
          color: "#fff",
          borderRadius: 6,
          padding: "4px 12px",
          cursor: "pointer",
          fontSize: 12,
          fontWeight: 600,
        }}
      >
        Dismiss
      </button>
    )}
    {!toast.persistent && (
      <div style={{ 
        fontSize: 11, 
        opacity: 0.7,
        flexShrink: 0,
      }}>
        {toast.type === "error" ? "⚠️" : "✓"}
      </div>
    )}
  </div>
)}

<style jsx>{`
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`}</style>

      <div
        style={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          padding: "20px 16px 32px",
        }}
      >
        <div
          style={{
            borderRadius: 16,
            background: "linear-gradient(135deg, #5B52B3 0%, #004AAD 100%)",
            padding: "12px 14px 18px",
            marginBottom: 20,
            boxShadow: "0 6px 18px rgba(15,23,42,0.25)",
            color: "#ffffff",
          }}
        >
          <div className="cdp-header-top-row">
            <div
              style={{
                marginTop: 2,
                fontSize: 15,
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <ClipboardList size={16} />
              Daily Collection Detail Sheet
            </div>
          </div>

          <div
            style={{
              marginTop: 10,
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: 8,
              alignItems: "center",
            }}
          >
            {[
              { icon: <ClipboardList size={14} />, label: "Total Items", value: total },
              { icon: <Clock size={14} />, label: "Pending", value: pending },
              { icon: <CheckCircle2 size={14} />, label: "Collected", value: collected },
            ].map(({ icon, label, value }) => (
              <div
                key={label}
                style={{
                  background: "rgba(15,23,42,0.12)",
                  borderRadius: 8,
                  padding: "6px 8px",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 12,
                }}
              >
                {icon}
                <div>
                  <div style={{ fontSize: 10, opacity: 0.85 }}>{label}</div>
                  <div style={{ fontWeight: 700 }}>{value}</div>
                </div>
              </div>
            ))}
          </div>

          <div
            style={{
              marginTop: 12,
              display: "flex",
              gap: 10,
              alignItems: "flex-start",
              flexWrap: "nowrap",
              paddingBottom: 6,
            }}
          >
            <div style={{ flex: "1 1 0", minWidth: 0 }}>
              <label
                style={{
                  fontSize: 11,
                  color: "rgba(255,255,255,0.8)",
                  display: "block",
                  marginBottom: 4,
                }}
              >
                <Calendar size={12} style={{ marginRight: 4 }} />
                Select Date
              </label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                style={{
                  width: "100%",
                  background: "rgba(255,255,255,0.12)",
                  border: "1px solid rgba(255,255,255,0.35)",
                  borderRadius: 10,
                  padding: "9px 12px",
                  color: "#fff",
                  fontSize: 13,
                  outline: "none",
                  height: 40,
                  boxSizing: "border-box",
                  WebkitAppearance: "none",
                }}
              />
            </div>

            <div style={{ flex: "1 1 0", minWidth: 0 }}>
              <label
                style={{
                  fontSize: 11,
                  color: "rgba(255,255,255,0.8)",
                  display: "block",
                  marginBottom: 4,
                }}
              >
                <Users size={12} style={{ marginRight: 4 }} />
                Select Admin
              </label>
              <div style={{ height: 40 }}>
                <SearchableSelect
                  options={[{ serviceNo: "", name: " Admins" }, ...ADMIN_OPTIONS]}
                  value={selectedAdmin}
                  onChange={(v) => {
                    if (typeof v === "object" && v.serviceNo === "") {
                      setSelectedAdmin("");
                    } else {
                      const formatted =
                        typeof v === "object" ? `${v.serviceNo}` : v;
                      setSelectedAdmin(formatted);
                    }
                  }}
                  placeholder="Admins"
                  id="select-admin-header"
                  keepPrimaryBackgroundAfterSelect
                  primaryBackgroundColor="#004AAD"
                  displayValue={adminDisplayValue || " Admins"}
                />
              </div>
            </div>
          </div>

          {(selectedAdmin || selectedChaser || searchTerm) && (
            <div
              style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}
            >
              {selectedAdmin && (
                <span
                  style={{
                    background: "rgba(255,255,255,0.18)",
                    borderRadius: 20,
                    padding: "3px 10px",
                    fontSize: 11,
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <Users size={10} />
                  {selectedAdmin}
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedAdmin("")}
                    onKeyDown={(e) => e.key === "Enter" && setSelectedAdmin("")}
                    style={{ cursor: "pointer", opacity: 0.8, marginLeft: 2 }}
                  >
                    ✕
                  </span>
                </span>
              )}
              {selectedChaser && (
                <span
                  style={{
                    background: "rgba(255,255,255,0.18)",
                    borderRadius: 20,
                    padding: "3px 10px",
                    fontSize: 11,
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <Truck size={10} />
                  {chaserDisplayValue}
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedChaser("")}
                    onKeyDown={(e) =>
                      e.key === "Enter" && setSelectedChaser("")
                    }
                    style={{ cursor: "pointer", opacity: 0.8, marginLeft: 2 }}
                  >
                    ✕
                  </span>
                </span>
              )}
              {searchTerm && (
                <span
                  style={{
                    background: "rgba(255,255,255,0.18)",
                    borderRadius: 20,
                    padding: "3px 10px",
                    fontSize: 11,
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <Search size={10} />
                  "{searchTerm}"
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={() => setSearchTerm("")}
                    onKeyDown={(e) => e.key === "Enter" && setSearchTerm("")}
                    style={{ cursor: "pointer", opacity: 0.8, marginLeft: 2 }}
                  >
                    ✕
                  </span>
                </span>
              )}
            </div>
          )}
        </div>

        <div
          style={{
            display: "flex",
            background: "rgba(255,255,255,0.95)",
            borderRadius: 12,
            padding: 4,
            marginBottom: 16,
            boxShadow: "0 2px 8px rgba(0,74,173,0.06)",
            border: "1px solid rgba(0,74,173,0.06)",
          }}
        >
          {[
            ["list", <><ClipboardList size={14} /> Collection List</>],
            [
              "form",
              editId ? (
                <><Edit size={14} /> Edit Item</>
              ) : (
                <><Plus size={14} /> Add Item</>
              ),
            ],
          ].map(([v, label]) => (
            <button
              key={v}
              onClick={() => (v === "form" ? handleNew() : setView("list"))}
              style={{
                flex: 1,
                border: "none",
                padding: "9px 12px",
                borderRadius: 9,
                cursor: "pointer",
                fontSize: 13,
                fontWeight: view === v ? 700 : 500,
                background:
                  view === v
                    ? "linear-gradient(135deg, #004AAD 0%, #1d4ed8 100%)"
                    : "transparent",
                color: view === v ? "#fff" : "#64748b",
                boxShadow: view === v ? "0 2px 8px rgba(0,74,173,0.25)" : "none",
                transition: "all 0.25s",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {view === "form" && (
          <div className="cdp-pop-in cdp-form-container" style={{ opacity: 0 }}>
            <div
              style={{
                background: "#fff",
                borderRadius: 20,
                boxShadow: "0 4px 24px rgba(0,74,173,0.08)",
                border: "1px solid rgba(0,74,173,0.06)",
                padding: 24,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  borderBottom: "1px solid rgba(0,74,173,0.08)",
                  paddingBottom: 16,
                  marginBottom: 20,
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    background: "rgba(0,74,173,0.08)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {editId ? (
                    <Edit size={18} color="#004AAD" />
                  ) : (
                    <Plus size={18} color="#004AAD" />
                  )}
                </div>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#1e293b" }}>
                    {editId ? "Edit Collection Item" : "New Collection Item"}
                  </div>
                  <div style={{ fontSize: 11, color: "#94a3b8" }}>
                    Fill in the details below
                  </div>
                </div>
              </div>

              <div className="cdp-form-top-grid" style={{ marginBottom: 20 }}>
                <Field label="PO No">
                  <PoCombobox
                    value={form.poNo}
                    onChange={(v) => setForm({ ...form, poNo: v })}
                    options={poOptions}
                    searchResults={poSearchResultsList}
                    onSelectResultItem={handleSelectPoResultItem}
                    loading={loadingOptions}
                    poSearchLoading={poSearchLoading}
                    poSearchResult={poSearchResult}
                  />
                </Field>

                <Field label="Handling Admin (Auto-set from PO No)">
                  <SearchableSelect
                    options={ADMIN_OPTIONS}
                    value={selectedAdmin}
                    onChange={(v) => {
                      const formatted =
                        typeof v === "object"
                          ? `${v.serviceNo} - ${v.name}`
                          : v;
                      setSelectedAdmin(formatted);
                      setForm((prev) => ({
                        ...prev,
                        handlingAdmin: formatted,
                      }));
                    }}
                    placeholder="-- Select Admin --"
                    id="select-admin-form"
                    usePrimaryPlaceholderStyle={false}
                    displayValue={selectedAdmin}
                  />
                </Field>
              </div>

              <div className="cdp-fields-grid">
                {formFields.map(([key, label, type]) => (
                  <div
                    key={key}
                    className={type === "textarea" ? "cdp-field-full" : undefined}
                  >
                    <Field label={label}>
                      {type === "status" ? (
                        <SearchableSelect
                          options={STATUS_OPTIONS}
                          value={form[key]}
                          onChange={(v) => setForm({ ...form, [key]: v })}
                          placeholder="Select status"
                          id={`status-${key}`}
                        />
                      ) : type === "area" ? (
                        <div className="cdp-area-highlight">
                          <SearchableSelect
                            options={AREA_OPTIONS}
                            value={form[key]}
                            onChange={(v) => setForm({ ...form, [key]: v })}
                            placeholder="Select Area"
                            id={`area-${key}`}
                            usePrimaryPlaceholderStyle={false}
                          />
                        </div>
                      ) : type === "moc" ? (
                        <MocCombobox
                          value={form[key]}
                          onChange={(v) => setForm({ ...form, [key]: v })}
                          options={mocOptions}
                          searchResults={mocSearchResultsList}
                          onSelectResultItem={handleSelectMocResultItem}
                          loading={loadingOptions}
                          mocSearchLoading={mocSearchLoading}
                          mocSearchResult={mocSearchResult}
                        />
                      ) : type === "readonly" ? (
                        <input
                          className="cdp-input"
                          style={{
                            ...inputSx,
                            background: "#f8fafc",
                            color: "#64748b",
                            cursor: "not-allowed",
                          }}
                          value={form[key] || ""}
                          readOnly
                          aria-readonly="true"
                        />
                      ) : type === "textarea" ? (
                        <div style={{ position: "relative" }}>
                          <textarea
                            className="cdp-input"
                            style={{
                              ...inputSx,
                              minHeight: "80px",
                              resize: "vertical",
                              fontFamily: "inherit",
                              borderColor: descriptionError ? "#ef4444" : undefined,
                            }}
                            value={form[key] || ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              setForm({ ...form, [key]: value });
                              if (value.length > MAX_DESCRIPTION_LENGTH) {
                                setDescriptionError(`Description cannot exceed ${MAX_DESCRIPTION_LENGTH} characters`);
                              } else {
                                setDescriptionError("");
                              }
                            }}
                            placeholder={label.replace(" *", "")}
                            rows={3}
                            maxLength={MAX_DESCRIPTION_LENGTH + 10}
                          />
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginTop: 4,
                            }}
                          >
                            <div style={{ fontSize: 11, color: "#94a3b8" }}>
                              {descriptionError ? (
                                <span style={{ color: "#ef4444", display: "flex", alignItems: "center", gap: 4 }}>
                                  <AlertCircle size={12} />
                                  {descriptionError}
                                </span>
                              ) : (
                                <span>
                                  {form.description?.length || 0} / {MAX_DESCRIPTION_LENGTH} characters
                                </span>
                              )}
                            </div>
                            <div style={{ fontSize: 11, color: "#94a3b8" }}>
                              {form.description?.length > MAX_DESCRIPTION_LENGTH * 0.8 && (
                                <span style={{ color: "#f59e0b" }}>
                                  {form.description?.length > MAX_DESCRIPTION_LENGTH * 0.9 ? (
                                    "⚠️ Almost at limit"
                                  ) : (
                                    "Getting long..."
                                  )}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <input
                          className="cdp-input"
                          style={inputSx}
                          value={form[key] || ""}
                          onChange={(e) =>
                            setForm({ ...form, [key]: e.target.value })
                          }
                          placeholder={label.replace(" *", "")}
                        />
                      )}
                    </Field>
                  </div>
                ))}
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 10,
                  marginTop: 20,
                  flexWrap: "wrap",
                }}
              >
                <button
                  onClick={handleSubmit}
                  disabled={!selectedAdmin || isSubmitting}
                  style={{
                    background: selectedAdmin 
                      ? "linear-gradient(135deg, #004AAD 0%, #1d4ed8 100%)"
                      : "#cbd5e1",
                    color: "#fff",
                    border: "none",
                    padding: "10px 24px",
                    borderRadius: 10,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor:
                      selectedAdmin && !isSubmitting 
                        ? "pointer"
                        : "not-allowed",
                    boxShadow: selectedAdmin 
                      ? "0 4px 12px rgba(0,74,173,0.3)"
                      : "none",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={14} className="cdp-spin" />
                      {editId ? "Updating..." : "Saving..."}
                    </>
                  ) : editId ? (
                    <>
                      <Edit size={14} />
                      Update Item
                    </>
                  ) : (
                    <>
                      <Save size={14} />
                      Save Item
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setView("list");
                    setForm(defaultForm);
                    setEditId(null);
                    setSelectedAdmin("");
                    setDescriptionError("");
                    setPoSearchResult(null);
                    setPoSearchResultsList([]);
                    setMocSearchResult(null);
                    setMocSearchResultsList([]);
                  }}
                  style={{
                    background: "#f1f5f9",
                    color: "#475569",
                    border: "none",
                    padding: "10px 20px",
                    borderRadius: 10,
                    fontSize: 14,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <XCircle size={14} />
                  Cancel
                </button>
              </div>
            </div>

            <style jsx>{`
              .cdp-form-container {
                width: 100%;
                margin: 0 auto;
              }
              .cdp-form-top-grid {
                display: grid;
                grid-template-columns: 1fr;
                gap: 14px;
              }
              .cdp-fields-grid {
                display: grid;
                grid-template-columns: 1fr;
                gap: 14px;
              }
              .cdp-field-full {
                grid-column: 1 / -1;
              }
              .cdp-area-highlight {
                background: rgba(0, 74, 173, 0.07);
                border: 1px solid rgba(0, 74, 173, 0.22);
                borderRadius: 10px;
                border-radius: 10px;
                padding: 4px;
              }
              @media (min-width: 640px) {
                .cdp-fields-grid {
                  grid-template-columns: 1fr 1fr;
                }
              }
              @media (min-width: 768px) {
                .cdp-form-top-grid {
                  grid-template-columns: 1fr 1fr;
                }
              }
              @media (min-width: 1024px) {
                .cdp-form-container {
                  max-width: 920px;
                }
                .cdp-fields-grid {
                  grid-template-columns: 1fr 1fr 1fr;
                }
              }
            `}</style>
          </div>
        )}

        {view === "list" && (
          <div className="cdp-fade-in" style={{ opacity: 0 }}>
            {loadingOptions ? (
              <div
                style={{
                  background: "#fff",
                  borderRadius: 20,
                  padding: "48px 24px",
                  textAlign: "center",
                  boxShadow: "0 4px 24px rgba(0,74,173,0.06)",
                }}
              >
                <Loader2
                  size={32}
                  color="#004AAD"
                  className="cdp-spin"
                  style={{ margin: "0 auto 12px" }}
                />
                <div style={{ fontSize: 14, color: "#64748b" }}>
                  Loading collection data...
                </div>
              </div>
            ) : filteredItems.length === 0 ? (
              <div
                style={{
                  background: "#fff",
                  borderRadius: 20,
                  padding: "48px 24px",
                  textAlign: "center",
                  boxShadow: "0 4px 24px rgba(0,74,173,0.06)",
                }}
              >
                <div style={{ fontSize: 56, marginBottom: 16 }}>📭</div>
                <div
                  style={{ fontSize: 16, color: "#64748b", marginBottom: 8 }}
                >
                  No collection items found
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: "#94a3b8",
                    marginBottom: 20,
                  }}
                >
                  {selectedAdmin || selectedChaser || searchTerm
                    ? "No items match the selected filters"
                    : "No items for the selected date"}
                </div>
                <button
                  onClick={handleNew}
                  style={{
                    background:
                      "linear-gradient(135deg, #004AAD 0%, #1d4ed8 100%)",
                    color: "#fff",
                    border: "none",
                    padding: "10px 24px",
                    borderRadius: 10,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <Plus size={14} />
                  Add First Item
                </button>
              </div>
            ) : (
              <>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "8px 4px",
                    marginBottom: 12,
                    fontSize: 13,
                    color: "#64748b",
                    flexWrap: "wrap",
                    gap: 8,
                  }}
                >
                  <span>
                    Showing {filteredItems.length} item{filteredItems.length !== 1 ? "s" : ""}
                    {searchTerm && ` matching "${searchTerm}"`}
                  </span>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    {searchTerm && (
                      <button
                        onClick={() => setSearchTerm("")}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#004AAD",
                          cursor: "pointer",
                          fontSize: 12,
                          fontWeight: 500,
                        }}
                      >
                        Clear search
                      </button>
                    )}
                    <button
                      onClick={handleExportExcel}
                      style={{
                        background: "linear-gradient(135deg, #10b981, #059669)",
                        color: "#fff",
                        border: "none",
                        padding: "7px 14px",
                        borderRadius: 8,
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        boxShadow: "0 2px 8px rgba(16,185,129,0.3)",
                      }}
                    >
                      <Download size={13} />
                      Export to Excel
                    </button>
                  </div>
                </div>

                <div style={{ 
                  display: "none", 
                  background: "#fff",
                  borderRadius: 16,
                  boxShadow: "0 2px 12px rgba(0,74,173,0.06)",
                  border: "1px solid rgba(0,74,173,0.06)",
                  overflow: "hidden",
                }} 
                className="desktop-table-view">
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ 
                      width: "100%", 
                      borderCollapse: "collapse",
                      fontSize: 13,
                      minWidth: "1000px",
                    }}>
                      <thead>
                        <tr style={{
                          background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                          borderBottom: "2px solid #e2e8f0",
                        }}>
                          <th style={{ padding: "12px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px" }}>#</th>
                          <th style={{ padding: "12px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px" }}>Status</th>
                          <th style={{ padding: "12px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px" }}>PO No</th>
                          <th style={{ padding: "12px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px" }}>MOC</th>
                          <th style={{ padding: "12px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px" }}>Description</th>
                          <th style={{ padding: "12px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px" }}>Item Description</th>
                          <th style={{ padding: "12px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px" }}>Quantity</th>
                          <th style={{ padding: "12px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px" }}>Supplier</th>
                          <th style={{ padding: "12px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px" }}>Handling Admin</th>
                          <th style={{ padding: "12px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px" }}>Chaser</th>
                          <th style={{ padding: "12px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px" }}>Chaser Remark</th>
                          <th style={{ padding: "12px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px" }}>Area</th>
                          <th style={{ padding: "12px 12px", textAlign: "center", fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px" }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredItems.map((item, idx) => (
                          <CollectionTableRow
                            key={item.id}
                            item={item}
                            idx={idx}
                            onEdit={handleEdit}
                            getBadgeClasses={getBadgeClasses}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div style={{ display: "block" }} className="mobile-card-view">
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {filteredItems.map((item, idx) => (
                      <CollectionCard
                        key={item.id}
                        item={item}
                        idx={idx}
                        onEdit={handleEdit}
                        getBadgeClasses={getBadgeClasses}
                      />
                    ))}
                  </div>
                </div>

                <style jsx>{`
                  @media (min-width: 768px) {
                    .desktop-table-view {
                      display: block !important;
                    }
                    .mobile-card-view {
                      display: none !important;
                    }
                  }
                  @media (max-width: 767px) {
                    .desktop-table-view {
                      display: none !important;
                    }
                    .mobile-card-view {
                      display: block !important;
                    }
                  }
                `}</style>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}